/**
 * 全社PLビュー(ステップ11・モックアップ準拠)。
 *
 * **各事業部モジュールの getMonthlySummary() の5行を単純合算するだけ。**
 * 事業部固有の項目は全社側に持ち込まない(CLAUDE.md の契約)。
 * 行をクリックすると事業部別の内訳を開く。階層へのドリルダウンはステップ14で追加する。
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { achievementRate, formatDiff, formatYen } from '@core/lib/money';
import { toMonthLabel } from '@core/lib/date';
import {
  sumSummaryLines,
  type Department,
  type DepartmentSummary,
  type PlLine,
  type SummaryLine,
} from '@core/lib/types';
import { getDepartmentModule, getDepartmentPendingSummary } from './moduleRegistry';
import DepartmentDrilldown from './DepartmentDrilldown';
import { fetchUsers } from '@core/masters/masterRepo';

type Row = { key: keyof SummaryLine; label: string; strong?: boolean };

const ROWS: Row[] = [
  { key: 'revenue', label: '売上' },
  { key: 'cogs', label: '原価' },
  { key: 'grossProfit', label: '粗利', strong: true },
  { key: 'expense', label: '経費' },
  { key: 'departmentProfit', label: '部署利益', strong: true },
];

/** 階層まで掘り下げられる行(粗利・部署利益は計算結果なので対象外) */
const DRILLABLE = new Set<keyof SummaryLine>(['revenue', 'cogs', 'expense']);

const PL_LINE_OF: Partial<Record<keyof SummaryLine, PlLine>> = {
  revenue: 'revenue',
  cogs: 'cogs',
  expense: 'expense',
};

type DepartmentResult = {
  department: Department;
  summary: DepartmentSummary;
  pending: SummaryLine;
};

function RateCell({ budget, actual }: { budget: number; actual: number }) {
  const rate = achievementRate(budget, actual);
  if (rate === null) {
    return (
      <div className="rate-wrap">
        <span className="num">—</span>
      </div>
    );
  }
  return (
    <div className="rate-wrap">
      <div className="rate-bar">
        <i className={rate < 95 ? 'low' : ''} style={{ width: `${Math.min(rate, 100)}%` }} />
      </div>
      <span className="num">{rate}%</span>
    </div>
  );
}

export default function CompanyPlPage() {
  const { accounts, departments, accountName } = useMasters();
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const { month } = usePeriod();
  const toast = useToast();
  const [results, setResults] = useState<DepartmentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (accounts.length === 0 || departments.length === 0) return;
    setLoading(true);
    try {
      const active = departments.filter((d) => d.active);
      const list = await Promise.all(
        active.map(async (department) => {
          const module = getDepartmentModule(department.id, accounts);
          const [summary, pending] = await Promise.all([
            module.getMonthlySummary(month),
            getDepartmentPendingSummary(department.id, month, accounts),
          ]);
          return { department, summary, pending };
        }),
      );
      setResults(list);
    } catch (e) {
      toast(e instanceof Error ? e.message : '全社PLを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [accounts, departments, month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // 取引一覧(根拠)で入力者名を出すために読む
  useEffect(() => {
    let alive = true;
    fetchUsers()
      .then((users) => alive && setUserNames(new Map(users.map((u) => [u.id, u.display_name]))))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const totals = useMemo(() => {
    return {
      budget: sumSummaryLines(results.map((r) => r.summary.budget)),
      actual: sumSummaryLines(results.map((r) => r.summary.actual)),
      pending: sumSummaryLines(results.map((r) => r.pending)),
    };
  }, [results]);

  function toggle(key: string) {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (loading) {
    return (
      <section className="screen">
        <div className="card">
          <p className="empty">読み込み中…</p>
        </div>
      </section>
    );
  }

  const hasPending = totals.pending.expense + totals.pending.revenue + totals.pending.cogs > 0;

  return (
    <section className="screen">
      <div className="card">
        <h3>
          全社PL({toMonthLabel(month)})
          <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
            — 行をクリックすると事業部別の内訳を開きます。実績は確定のみ、見込みは別列です
          </span>
        </h3>
        <table>
          <thead>
            <tr>
              <th style={{ width: '32%' }}>項目</th>
              <th className="num">予算</th>
              <th className="num">実績</th>
              <th className="num">見込み</th>
              <th className="num">差異</th>
              <th className="num" style={{ width: 130 }}>
                達成率
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const budget = totals.budget[row.key];
              const actual = totals.actual[row.key];
              const pending = totals.pending[row.key];
              const diff = actual - budget;
              const isOpen = openRows.has(row.key);
              return (
                <Fragment key={row.key}>
                  <tr
                    key={row.key}
                    className="pl-row"
                    onClick={() => toggle(row.key)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="pl-name">
                        <span className={`chev${isOpen ? ' open' : ''}`}>▶</span>
                        <span style={{ fontWeight: row.strong ? 700 : 600 }}>{row.label}</span>
                      </div>
                    </td>
                    <td className="num">{formatYen(budget)}</td>
                    <td className="num" style={{ fontWeight: row.strong ? 700 : 400 }}>
                      {formatYen(actual)}
                    </td>
                    <td className="num hint">{pending === 0 ? '—' : formatYen(pending)}</td>
                    <td className={`num ${diff >= 0 ? 'pos' : 'neg'}`}>{formatDiff(diff)}</td>
                    <td>
                      <RateCell budget={budget} actual={actual} />
                    </td>
                  </tr>

                  {isOpen &&
                    results.map((r) => {
                      const dBudget = r.summary.budget[row.key];
                      const dActual = r.summary.actual[row.key];
                      const dPending = r.pending[row.key];
                      const dDiff = dActual - dBudget;
                      const deptKey = `${row.key}:${r.department.id}`;
                      const deptOpen = openRows.has(deptKey);
                      // 粗利・部署利益は計算結果のため、階層への掘り下げは行わない
                      const canDrill = DRILLABLE.has(row.key) && dActual !== 0;
                      return (
                        <Fragment key={deptKey}>
                          <tr
                            key={deptKey}
                            className={canDrill ? 'pl-row' : undefined}
                            onClick={canDrill ? () => toggle(deptKey) : undefined}
                            style={canDrill ? { cursor: 'pointer' } : undefined}
                          >
                            <td>
                              <div className="pl-name" style={{ paddingLeft: 26 }}>
                                {canDrill ? (
                                  <span className={`chev${deptOpen ? ' open' : ''}`}>▶</span>
                                ) : (
                                  <span className="chev" />
                                )}
                                <span className="lvl">事業部</span>
                                {r.department.name}
                              </div>
                            </td>
                            <td className="num">{formatYen(dBudget)}</td>
                            <td className="num">{formatYen(dActual)}</td>
                            <td className="num hint">{dPending === 0 ? '—' : formatYen(dPending)}</td>
                            <td className={`num ${dDiff >= 0 ? 'pos' : 'neg'}`}>{formatDiff(dDiff)}</td>
                            <td>
                              <RateCell budget={dBudget} actual={dActual} />
                            </td>
                          </tr>
                          {canDrill && deptOpen && (
                            <DepartmentDrilldown
                              key={`${deptKey}-drill`}
                              department={r.department}
                              month={month}
                              plLine={PL_LINE_OF[row.key] ?? null}
                              accounts={accounts}
                              accountName={accountName}
                              userNames={userNames}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        <p className="hint" style={{ marginTop: 10 }}>
          実績は<b>確定した取引のみ</b>を集計しています。
          {hasPending && '「見込み」は承認済・領収書提出済で、まだ確定していない分です(実績には含みません)。'}
          階層へのドリルダウンと取引一覧はステップ14で追加します。
        </p>
      </div>

      <div className="card">
        <h3>事業部別サマリー(共通サマリー5行)</h3>
        <table>
          <thead>
            <tr>
              <th>事業部</th>
              <th className="num">売上</th>
              <th className="num">原価</th>
              <th className="num">粗利</th>
              <th className="num">経費</th>
              <th className="num">部署利益</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.department.id}>
                <td>{r.department.name}</td>
                <td className="num">{formatYen(r.summary.actual.revenue)}</td>
                <td className="num">{formatYen(r.summary.actual.cogs)}</td>
                <td className="num">{formatYen(r.summary.actual.grossProfit)}</td>
                <td className="num">{formatYen(r.summary.actual.expense)}</td>
                <td
                  className={`num ${r.summary.actual.departmentProfit >= 0 ? 'pos' : 'neg'}`}
                  style={{ fontWeight: 700 }}
                >
                  {formatYen(r.summary.actual.departmentProfit)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 700 }}>全社合計</td>
              <td className="num" style={{ fontWeight: 700 }}>{formatYen(totals.actual.revenue)}</td>
              <td className="num" style={{ fontWeight: 700 }}>{formatYen(totals.actual.cogs)}</td>
              <td className="num" style={{ fontWeight: 700 }}>{formatYen(totals.actual.grossProfit)}</td>
              <td className="num" style={{ fontWeight: 700 }}>{formatYen(totals.actual.expense)}</td>
              <td
                className={`num ${totals.actual.departmentProfit >= 0 ? 'pos' : 'neg'}`}
                style={{ fontWeight: 700 }}
              >
                {formatYen(totals.actual.departmentProfit)}
              </td>
            </tr>
          </tbody>
        </table>
        <p className="hint" style={{ marginTop: 10 }}>
          この表は各事業部モジュールが返す5行をそのまま並べたものです。事業部固有の項目は全社側に出しません。
        </p>
      </div>
    </section>
  );
}
